import { useState } from 'react';
import type { FormEvent } from 'react';
import { esquemaCorreo } from '@copathy/shared';
import { Boton } from './Boton';
import { Campo } from './Campo';
import { Correo, Flecha } from './iconos';

/**
 * Segundo panel del riel de la pantalla de acceso.
 *
 * `/restablecer` sí será una página aparte, porque es a donde llega el enlace
 * del correo. Pedir el enlace, en cambio, ocurre acá mismo: salir de la pantalla
 * de acceso para pedir un código y volver es un salto que no aporta nada.
 */
export function FormularioRecuperar({ alVolver }: { alVolver: () => void }) {
  const [correo, setCorreo] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    const r = esquemaCorreo.safeParse(correo);
    if (!r.success) {
      setError(r.error.issues[0]?.message ?? 'Revisa el correo.');
      return;
    }
    setError(undefined);
    setEnviando(true);
    try {
      // El endpoint todavía no existe (M1 sigue incompleto). Cuando exista,
      // responde 204 exista o no el correo: responder distinto convertiría esto
      // en un verificador de quién está registrado.
      await new Promise((r) => setTimeout(r, 600));
      setEnviado(true);
    } finally {
      setEnviando(false);
    }
  }

  if (enviado) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-marino-800">Revisa tu correo</h2>
        <p className="mt-2 text-sm leading-relaxed text-marino-400">
          Si <strong className="text-marino-700">{correo}</strong> tiene una cuenta, te llega
          un enlace para crear una contraseña nueva. El enlace vence en una hora.
        </p>
        <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          Todavía no se envía nada: el correo saliente es parte de un módulo que no está
          construido.
        </p>
        <Boton variante="secundario" onClick={alVolver} className="mt-6">
          <Flecha size={16} /> Volver a ingresar
        </Boton>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-marino-800">Recuperar contraseña</h2>
      <p className="mt-1.5 text-sm text-marino-400">
        Escribe tu correo y te enviamos un enlace para crear una nueva.
      </p>

      <form onSubmit={enviar} className="mt-6 space-y-5">
        <Campo
          etiqueta="Correo electrónico"
          type="email"
          autoComplete="email"
          icono={<Correo size={18} />}
          value={correo}
          onChange={(e) => setCorreo(e.target.value)}
          error={error}
        />

        <Boton type="submit" cargando={enviando} className="w-full">
          {enviando ? 'Enviando…' : 'Enviar enlace'}
        </Boton>
      </form>

      <button
        type="button"
        onClick={alVolver}
        className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-marino-600 hover:text-naranja-600"
      >
        <Flecha size={16} /> Volver a ingresar
      </button>
    </div>
  );
}
